import React, { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import BannerCustom from "components/Banners/BannerCustom";
import useAppDispatch, { useReduxDispatch } from "hooks/useAppDispatch";
import sandboxIcon from "img/illustrations/general/sandbox.svg";
import {
  clearSandboxError,
  fetchSandboxInfo,
  switchBackFromSandbox,
  switchToSandbox,
} from "../model/sandboxSlice";
import "../Sandbox.sass";

export default function SandboxMenu({
  onCreate,
  onManage,
  onCloseProfile,
}) {
  const { t } = useTranslation("productSettings");
  const dispatch = useAppDispatch();
  const reduxDispatch = useReduxDispatch();
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const {
    sandboxes = [],
    isSandboxSession,
    originalCompanyName,
    loaded,
    fetching,
    switching,
    error,
  } = useSelector((state) => state.sandbox || {});
  const sandbox = sandboxes[0] || null;

  useEffect(() => {
    if (!loaded) {
      dispatch(fetchSandboxInfo()).catch(() => {});
    }
  }, [dispatch, loaded]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const switchSession = useCallback(
    async (switchBack = false) => {
      try {
        await dispatch(
          switchBack ? switchBackFromSandbox() : switchToSandbox(),
        );
        window.location.reload();
      } catch {
        // Error remains visible in the submenu.
      }
    },
    [dispatch],
  );

  const togglePanel = () => {
    if (open) {
      setOpen(false);
      return;
    }
    reduxDispatch(clearSandboxError());
    setOpen(true);
    dispatch(fetchSandboxInfo()).catch(() => {});
  };

  return (
    <div className="SandboxMenu" ref={panelRef}>
      <button
        type="button"
        className={`userProfile-content-option ${open ? "is-active" : ""}`}
        onClick={togglePanel}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <img src={sandboxIcon} alt="" />
        <span>
          {isSandboxSession
            ? t("sandbox.navigation.currentSandbox", {
                defaultValue: "Sandbox active",
              })
            : t("sandbox.navigation.switchTo", {
                defaultValue: "Switch to sandbox",
              })}
        </span>
        <span className="SandboxMenu__chevron" aria-hidden="true">›</span>
      </button>

      {open ? (
        <div className="SandboxMenu__panel" role="menu">
          {fetching && !loaded ? (
            <div className="SandboxMenu__loading">
              <Spinner size="small" />
            </div>
          ) : (
            <>
              {error ? (
                <div className="SandboxMenu__error">
                  <BannerCustom status="critical" message={error} />
                </div>
              ) : null}

              {isSandboxSession ? (
                <button
                  type="button"
                  className="SandboxMenu__row"
                  onClick={() => switchSession(true)}
                  disabled={switching}
                  role="menuitem"
                >
                  <span className="SandboxMenu__rowIcon" aria-hidden="true">←</span>
                  <span>
                    {t("sandbox.navigation.switchBack", {
                      defaultValue: "Switch back to live company",
                    })}
                    {originalCompanyName ? ` — ${originalCompanyName}` : ""}
                  </span>
                </button>
              ) : null}

              {sandbox ? (
                <button
                  type="button"
                  className="SandboxMenu__row"
                  onClick={() => switchSession(false)}
                  disabled={switching || isSandboxSession}
                  role="menuitem"
                >
                  <span className="SandboxMenu__sandboxMark" aria-hidden="true">
                    <img src={sandboxIcon} alt="" />
                  </span>
                  <span className="SandboxMenu__rowText">
                    <strong>{sandbox.name}</strong>
                    <small>
                      {isSandboxSession
                        ? t("sandbox.navigation.active", {
                            defaultValue: "Active sandbox",
                          })
                        : t("sandbox.navigation.open", {
                            defaultValue: "Switch to this sandbox",
                          })}
                    </small>
                  </span>
                </button>
              ) : !error ? (
                <div className="SandboxMenu__empty">
                  {t("sandbox.navigation.empty", {
                    defaultValue: "No sandbox has been created yet.",
                  })}
                </div>
              ) : null}

              <div className="SandboxMenu__divider" />

              <button
                type="button"
                className="SandboxMenu__row"
                onClick={() => {
                  setOpen(false);
                  onCloseProfile?.();
                  onCreate?.();
                }}
                disabled={
                  Boolean(sandbox) ||
                  switching ||
                  fetching ||
                  Boolean(error) ||
                  !loaded
                }
                title={
                  sandbox
                    ? t("sandbox.navigation.singleLimit", {
                        defaultValue:
                          "Only one sandbox can be created for a company.",
                      })
                    : undefined
                }
                role="menuitem"
              >
                <span className="SandboxMenu__rowIcon" aria-hidden="true">+</span>
                <span>
                  {t("sandbox.navigation.create", {
                    defaultValue: "Create sandbox",
                  })}
                </span>
              </button>

              <button
                type="button"
                className="SandboxMenu__row"
                onClick={() => {
                  setOpen(false);
                  onManage?.();
                  onCloseProfile?.();
                }}
                role="menuitem"
              >
                <span className="SandboxMenu__sandboxMark" aria-hidden="true">
                  <img src={sandboxIcon} alt="" />
                </span>
                <span>
                  {t("sandbox.navigation.manage", {
                    defaultValue: "Manage sandboxes",
                  })}
                </span>
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
